/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\App.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
 * Description du fichier:
 * Composant principal de l'application qui définit toutes les routes et la navigation.
 * 
 * Relations avec d'autres fichiers:
 * - Importe et utilise tous les composants de pages principaux (Dashboard, Clients, Devis, etc.)
 * - Utilise le composant Layout pour la structure commune des pages authentifiées
 * - Intègre le contexte d'authentification (AuthProvider) pour gérer l'état de connexion
 * 
 * Actions déclenchées:
 * - Gère la redirection vers la page de connexion pour les utilisateurs non authentifiés
 * - Configure les routes protégées et publiques de l'application
 * - Définit la hiérarchie de navigation entre les différentes sections
 */
import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Devis from './pages/Devis';
import Parametres from './pages/Parametres';
import Entreprise from './pages/Entreprise';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
// import NouveauDevis from './pages/Devis/NouveauDevis';
import ConfigurateurArticle from './pages/Devis/ConfigurateurArticle/index';
// import DetailsDevis from './pages/Devis/DetailsDevis'; // OBSOLETE - Route supprimée, utiliser /devis/configurateur/:id à la place
import ClientDetails from './pages/Clients/ClientDetails';
import NouveauClient from './pages/Clients/NouveauClient';
import Abonnement from './pages/Abonnement';
import AdminDashboard from './pages/Admin';
import CatalogueFournisseur from './pages/CatalogueFournisseur';
import { AuthProvider, AuthContext } from './contexts/AuthContext';
import { FallbackAlertsContainer } from './components/Common/FallbackAlert';
import FallbackStatusIndicator from './components/Common/FallbackStatusIndicator';
import BetaBanner from './components/Beta/BetaBanner';
import FeedbackModal from './components/Beta/FeedbackModal';
import FeedbackButton from './components/Beta/FeedbackButton';
import ArticleConfigurationLight from './pages/Light/ArticleConfigurationLight';
import AccessBanner from './components/Common/AccessBanner';
import AccessDeniedModal from './components/Common/AccessDeniedModal';
import CommandesFournisseur from './pages/CommandesFournisseur';
import CommandeFournisseurDetail from './pages/CommandeFournisseurDetail';

const AppContent: React.FC = () => {
  // Récupération de l'état d'authentification depuis le contexte
  const { isAuthenticated, loading, user } = React.useContext(AuthContext);
  const location = useLocation();
  const isLightRoute = location.pathname.startsWith('/light');
  const isLightHost = typeof window !== 'undefined' && window.location.hostname.startsWith('light.');

  // État pour gérer l'ouverture du modal de feedback
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = React.useState(false);

  // Si le domaine est light.* mais qu'on n'est pas encore sur /light, rediriger vers la route publique Light
  if (isLightHost && !isLightRoute) {
    return <Navigate to={`/light${location.search || ''}`} replace />;
  }

  // Afficher un indicateur de chargement pendant la vérification de l'authentification
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        Chargement...
      </div>
    );
  }

  return (
    <>
      {/* Conteneur global des alertes de fallback */}
      <FallbackAlertsContainer />
      
      {/* Indicateur visuel des fallbacks pour les tests */}
      <FallbackStatusIndicator />

      {/* Bannière d'accès selon le mode (preview/trial/read_only) */}
      {!isLightRoute && <AccessBanner />}
      {/* Modal global sur erreur access_denied */}
      {!isLightRoute && <AccessDeniedModal />}
      
      {/* Bandeau version bêta - Affiché pour les utilisateurs authentifiés, hors parcours Light */}
      {isAuthenticated && user?.isBeta && !isLightRoute && (
        <BetaBanner onFeedbackClick={() => setIsFeedbackModalOpen(true)} />
      )}
      
      {/* Modal de feedback */}
      {user?.isBeta && (
        <FeedbackModal
          isOpen={isFeedbackModalOpen}
          onClose={() => setIsFeedbackModalOpen(false)}
        />
      )}
      
      {/* Bouton flottant de feedback - Affiché pour les utilisateurs authentifiés */}
      {isAuthenticated && user?.isBeta && (
        <FeedbackButton onClick={() => setIsFeedbackModalOpen(true)} />
      )}
      
      <Routes>
        {/* Routes publiques */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/light" element={<ArticleConfigurationLight />} />

        {/* Routes protégées */}
        <Route
          path="/"
          element={
            isAuthenticated ? <Layout /> : <Navigate to="/login" replace />
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="devis" element={<Devis />} />
          <Route path="devis/nouveau" element={<ConfigurateurArticle />} />
          {/* Route obsolète /devis/:id supprimée - utiliser /devis/configurateur/:id à la place */}
          <Route path="devis/:id/edit" element={<ConfigurateurArticle />} />
          <Route path="devis/configurateur/:id" element={<ConfigurateurArticle />} />
          <Route path="clients" element={<Clients />} />
          <Route path="clients/nouveau" element={<NouveauClient />} />
          <Route path="clients/:id" element={<ClientDetails />} />
          <Route path="clients/:id/edit" element={<NouveauClient />} />
          <Route path="commandes-fournisseur" element={<CommandesFournisseur />} />
          <Route path="commandes-fournisseur/:id" element={<CommandeFournisseurDetail />} />
          <Route path="parametres/*" element={<Parametres />} />
          <Route path="catalogue/:fournisseur" element={<CatalogueFournisseur />} />
          <Route path="entreprise" element={<Entreprise />} />
          <Route path="abonnement" element={<Abonnement />} />
          <Route path="admin/*" element={<AdminDashboard />} />
        </Route>

      {/* Redirection pour les routes inconnues */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
